import { useEffect } from 'react';
import { useApplicationSettingsContext } from '../../contexts/ApplicationSettingsContext';
import { usePermissionCheck } from '../../hooks/usePermissionCheck';

// Debug flag to control logging - set to false to disable logs
const enableTabsDebug = false;

interface BookingTabsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  isInventoryAppInstalled?: boolean;
  showMaterialsInBookings?: boolean;
  isExpensesAppInstalled?: boolean;
  isPayrollAppInstalled?: boolean;
  showFlatRateInBookings?: boolean;
  /** Show a "Field Quote" tab (directly after Overview) when a field quote is linked. */
  hasFieldQuote?: boolean;
}

export default function BookingTabs({ activeTab, onTabChange, isInventoryAppInstalled = false, showMaterialsInBookings = false, isExpensesAppInstalled = false, isPayrollAppInstalled = false, showFlatRateInBookings = false, hasFieldQuote = false }: BookingTabsProps) {
  const { disabledPages } = useApplicationSettingsContext();
  const { canAccess } = usePermissionCheck();

  // Debug logging
  console.log('🔍 BookingTabs received props:', {
    isPayrollAppInstalled,
    showFlatRateInBookings,
    isInventoryAppInstalled,
    showMaterialsInBookings,
    isExpensesAppInstalled
  });

  // Check if pages are disabled in application settings or by permissions
  const isPnLDisabled = !canAccess.bookingProfitLoss;
  // Expenses tab is visible if EITHER Inventory app OR Expenses app is installed
  // But still need to check inventory setting if inventory app is installed
  const shouldShowExpensesTab = (isInventoryAppInstalled && showMaterialsInBookings) || isExpensesAppInstalled;
  const isMaterialsDisabled = disabledPages.includes('materials') || !canAccess.bookingMaterials || !shouldShowExpensesTab;
  const isFlatRateDisabled = disabledPages.includes('flat-rates') || !canAccess.bookingRatebook || !isPayrollAppInstalled || !showFlatRateInBookings;

  console.log('🔍 BookingTabs tab calculations:', {
    isFlatRateDisabled,
    reasons: {
      disabledInSettings: disabledPages.includes('flat-rates'),
      noPermission: !canAccess.bookingRatebook,
      appNotInstalled: !isPayrollAppInstalled,
      settingDisabled: !showFlatRateInBookings
    }
  });

  if (enableTabsDebug) {
    console.log('📊 BookingTabs: Tab visibility', {
      pnl: !isPnLDisabled,
      materials: !isMaterialsDisabled,
      flatRate: !isFlatRateDisabled
    });
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    // Field Quote always renders next after Overview, regardless of other enabled
    // tabs, but only when a field quote is linked to this booking.
    ...(hasFieldQuote ? [{ id: 'field-quote', label: 'Field Quote' }] : []),
    // Only show expenses tab if enabled in application settings and user has permission
    ...(isMaterialsDisabled ? [] : [{ id: 'materials', label: 'Expenses' }]),
    // Only show flat rates tab if enabled in application settings and user has permission
    ...(isFlatRateDisabled ? [] : [{ id: 'flat-rates', label: 'RateBook' }]),
    // Only show profit & loss tab if user has permission
    ...(isPnLDisabled ? [] : [{ id: 'profit-loss', label: 'Profit & Loss' }])
  ];

  // If the active tab is not in the available tabs, switch to overview
  useEffect(() => {
    if (activeTab !== 'overview' && !tabs.some(tab => tab.id === activeTab)) {
      if (enableTabsDebug) console.log('⚠️ BookingTabs: Active tab not available, switching to overview');
      onTabChange('overview');
    }
  }, [activeTab, tabs, onTabChange]);

  return (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-5xl mx-auto">
        <nav className="flex overflow-x-auto scrollbar-hide" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`flex-1 min-w-[80px] py-3 sm:py-4 px-2 sm:px-4 border-b-2 text-xs sm:text-sm font-bold text-center whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-700 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-blue-700 hover:border-blue-400 hover:bg-blue-50'
              }`}
              style={{ touchAction: 'manipulation' }}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>
      
      {/* Add custom styles for hiding scrollbar but allowing scroll */}
      <style jsx global>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;  /* Firefox */
        }
      `}</style>
    </div>
  );
}